import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'

/**
 * The people. Every action here asks for a reason before it runs — the prompt is in-app rather
 * than a browser dialog, and the reason lands in the audit table with the change.
 */
export default function Users({ onExpired }) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [users, setUsers] = useState([])
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState(null)
  const [reason, setReason] = useState('')
  const [adding, setAdding] = useState(null)

  const load = useCallback(() => {
    api
      .users(query)
      .then((result) => {
        setUsers(result)
        setError(null)
      })
      .catch((failure) => {
        onExpired(failure)
        setError(failure.message)
      })
  }, [query, onExpired])

  useEffect(() => {
    const timer = setTimeout(load, 250)
    return () => clearTimeout(timer)
  }, [load])

  async function run(action) {
    setBusy(true)
    setError(null)
    setNotice(null)

    try {
      await action()
      setNotice(t('users.done'))
      setAsking(null)
      setReason('')
      load()
    } catch (failure) {
      onExpired(failure)
      setError(failure.message)
    } finally {
      setBusy(false)
    }
  }

  const ask = (user, action, label) => setAsking({ user, action, label })

  return (
    <>
      <section className="card">
        <div className="row">
          <input
            type="search"
            className="search grow"
            placeholder={t('users.search')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button type="button" className="button" onClick={() => setAdding({ role: 'Owner' })}>
            {t('users.add')}
          </button>
        </div>

        {error && <p className="status bad">{error}</p>}
        {notice && <p className="status good">{notice}</p>}
      </section>

      {adding && (
        <section className="card">
          <h2>{t('users.add')}</h2>
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault()
              run(async () => {
                await api.addUser({
                  email: adding.email,
                  password: adding.password,
                  displayName: adding.displayName || null,
                  tenantId: adding.tenantId ? Number(adding.tenantId) : null,
                  role: adding.role,
                  reason: adding.reason,
                })
                setAdding(null)
              })
            }}
          >
            <input required type="email" placeholder={t('users.email')} onChange={(e) => setAdding({ ...adding, email: e.target.value })} />
            <input required minLength={10} type="text" placeholder={t('users.password')} onChange={(e) => setAdding({ ...adding, password: e.target.value })} />
            <input type="text" placeholder={t('users.name')} onChange={(e) => setAdding({ ...adding, displayName: e.target.value })} />
            <input type="number" placeholder={t('users.tenantId')} onChange={(e) => setAdding({ ...adding, tenantId: e.target.value })} />
            <select value={adding.role} onChange={(e) => setAdding({ ...adding, role: e.target.value })}>
              <option value="Owner">Owner</option>
              <option value="Admin">Admin</option>
              <option value="Editor">Editor</option>
            </select>
            <input required minLength={5} type="text" placeholder={t('users.reason')} onChange={(e) => setAdding({ ...adding, reason: e.target.value })} />
            <div className="row">
              <button type="submit" className="button" disabled={busy}>
                {t('users.create')}
              </button>
              <button type="button" className="button quiet" onClick={() => setAdding(null)}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </section>
      )}

      {asking && (
        <section className="card">
          <h2>{asking.label}</h2>
          <p className="muted small">{asking.user.email ?? asking.user.displayName ?? `#${asking.user.id}`}</p>
          <form
            className="row"
            onSubmit={(event) => {
              event.preventDefault()
              run(() => asking.action(reason))
            }}
          >
            <input
              required
              minLength={5}
              className="grow"
              placeholder={t('users.reason')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <button type="submit" className="button" disabled={busy || reason.length < 5}>
              {t('users.confirm')}
            </button>
            <button type="button" className="button quiet" onClick={() => setAsking(null)}>
              {t('common.cancel')}
            </button>
          </form>
        </section>
      )}

      {users.map((user) => (
        <section key={user.id} className="card user">
          <div className="user__head">
            <strong>{user.displayName ?? user.email ?? user.fbUserId}</strong>
            {user.isPlatformAdmin && <span className="tag">admin</span>}
            {!user.emailVerified && user.email && <span className="tag warn">{t('users.unverified')}</span>}
          </div>

          <p className="muted small">
            #{user.id} · {user.email ?? '—'} {user.fbUserId ? `· FB ${user.fbUserId}` : ''} ·{' '}
            {user.hasPassword ? t('users.hasPassword') : t('users.noPassword')} ·{' '}
            {t('users.lastLogin')}: {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('mk-MK') : t('users.never')}
          </p>

          <p className="small">
            {user.tenants.map((tenant) => (
              <span key={tenant.tenantId}>
                <a href={`#/tenant/${tenant.tenantId}`}>{tenant.name}</a> ({tenant.role}){' '}
              </span>
            ))}
          </p>

          <div className="row wrap">
            <button
              type="button"
              className="button quiet"
              onClick={() => ask(user, (why) => api.revokeSessions(user.id, why), t('users.revoke'))}
            >
              {t('users.revoke')}
            </button>
            <button
              type="button"
              className="button quiet"
              onClick={() => ask(user, (why) => api.unlockUser(user.id, why), t('users.unlock'))}
            >
              {t('users.unlock')}
            </button>
            {user.email && !user.emailVerified && (
              <button
                type="button"
                className="button quiet"
                onClick={() => ask(user, (why) => api.verifyEmail(user.id, why), t('users.verify'))}
              >
                {t('users.verify')}
              </button>
            )}
            <button
              type="button"
              className={user.isPlatformAdmin ? 'button danger' : 'button quiet'}
              onClick={() =>
                ask(
                  user,
                  (why) => api.setPlatformAdmin(user.id, !user.isPlatformAdmin, why),
                  user.isPlatformAdmin ? t('users.revokeAdmin') : t('users.grantAdmin'),
                )
              }
            >
              {user.isPlatformAdmin ? t('users.revokeAdmin') : t('users.grantAdmin')}
            </button>

            {user.tenants.map((tenant) => (
              <select
                key={tenant.tenantId}
                value={tenant.role}
                onChange={(event) =>
                  ask(
                    user,
                    (why) =>
                      api.setRole(user.id, { tenantId: tenant.tenantId, role: event.target.value, reason: why }),
                    t('users.roleIn', { name: tenant.name, role: event.target.value }),
                  )
                }
              >
                <option value="Owner">Owner</option>
                <option value="Admin">Admin</option>
                <option value="Editor">Editor</option>
              </select>
            ))}
          </div>
        </section>
      ))}
    </>
  )
}
